import React, { Suspense, lazy, useEffect, useState } from "react";
import { Routes, Route, Navigate, useLocation, useParams, useNavigate } from "react-router-dom";
import { APP_VERSION } from "@/utils/cacheControl";
import ProtectedRoute from "@/components/auth/ProtectedRoute";
import ProtectedSuperadminRoute from "@/components/auth/ProtectedSuperadminRoute";

// Pages
import Index from "@/pages/Index";
import Auth from "@/pages/Auth";
import VerifyMagicLink from "@/pages/VerifyMagicLink";
import AcceptInvitation from "@/pages/AcceptInvitation";
import ProfileDetail from "@/pages/ProfileDetail";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminEditProfile from "@/pages/AdminEditProfile";
import CSVUploaderPage from "@/pages/CSVUploaderPage";
import NotFound from "@/pages/NotFound";
import About from "@/pages/About";
import ClaimProfile from "@/pages/ClaimProfile";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import LearnMore from "@/pages/learnmore";
import LearnMoreVetting from "@/pages/LearnMoreVetting";
import PortfolioItemPage from "@/pages/PortfolioItemPage";
import ServiceResultsPage from "@/pages/ServiceResultsPage";
import Pricing from "@/pages/Pricing";
import Home from "@/pages/get_verified";
import Designers from "@/pages/Designers";

// New Dashboard Layout and Pages
import AppLayout from "@/components/layout/AppLayout";
import OverviewPage from "@/pages/dashboard/OverviewPage";
import ProfilePage from "@/pages/dashboard/ProfilePage";
import PortfolioPage from "@/pages/dashboard/PortfolioPage";
import ExperiencePage from "@/pages/dashboard/ExperiencePage";
import NotificationsPage from "@/pages/dashboard/NotificationsPage";
import SettingsPage from "@/pages/dashboard/SettingsPage";
import CaseStudyEditorPage from "@/pages/dashboard/CaseStudyEditorPage";
import CaseStudyEditorPageV2 from "@/pages/dashboard/CaseStudyEditorPageV2";
import CaseStudyPreviewPage from "@/pages/dashboard/CaseStudyPreviewPage";
import ShotEditorPage from "@/pages/dashboard/ShotEditorPage";

// Onboarding Components
import OnboardingFlow from "@/components/onboarding/OnboardingFlow";
import OnboardingWrapper from "@/components/onboarding/OnboardingWrapper";
import OnboardingRouteGuard from "@/components/onboarding/OnboardingRouteGuard";

// Superadmin pages
import Unauthorized from "@/pages/Unauthorized";
import SuperadminDashboard from "@/pages/SuperadminDashboard";
import SuperadminUsers from "@/pages/SuperadminUsers";
import SuperadminConsentLogs from "@/pages/SuperadminConsentLogs";
import SuperadminReports from "@/pages/SuperadminReports";

// Configure version in a way that changes on every build
const APP_BUILD_ID = APP_VERSION;

// console.log('[AppRoutes] Using build IDs:', { APP_BUILD_ID });

// Wrapper for shareable modal route
import { useQueryClient } from "@tanstack/react-query";
import PortfolioItemModal from "@/components/profile/PortfolioItemModal";
import { useProfile } from "@/hooks/domains/profiles/useProfile";
const ProfileWithPortfolioModal = () => {
  const { username, portfolio_slug } = useParams<{ username: string; portfolio_slug: string }>();
  const { data: profile } = useProfile(username);
  const [selectedItem, setSelectedItem] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  // Detect if this is a shared/direct modal link (no background location)
  const isSharedModal = !location.state || !location.state.background;

  // Slugify utility (should match PortfolioGallery)
  function slugify(text) {
    return text
      .toString()
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^\w\-]+/g, '')
      .replace(/--+/g, '-')
      .replace(/^-+/, '')
      .replace(/-+$/, '');
  }
  function getPortfolioSlug(item) {
    return item.slug || slugify(item.title || item.id);
  }

  useEffect(() => {
    if (profile && portfolio_slug) {
      const item = profile.portfolio_items?.find(i => getPortfolioSlug(i) === portfolio_slug);
      setSelectedItem(item || null);
    }
  }, [profile, portfolio_slug]);

  if (!profile) return null;
  const handleClose = () => {
    if (isSharedModal) {
      queryClient.invalidateQueries({ queryKey: ["profile", username] });
    }
    navigate(`/u/${profile.username_slug}`);
  };
  return (
    <>
      <ProfileDetail profileOverride={profile} openPortfolioModal={selectedItem} onClosePortfolioModal={handleClose} />
      {selectedItem && (
        <PortfolioItemModal
          item={selectedItem}
          isOpen={!!selectedItem}
          onClose={handleClose}
          username={profile.username || ""}
          username_slug={profile.username_slug || ""}
        />
      )}
    </>
  );
};

const AppRoutes: React.FC = () => {
  const location = useLocation();
  
  const state = location.state as { background?: Location };

  return (
    <>
      <Routes location={state?.background || location}>
        {/* Public routes */}
        <Route path="/" element={<Home key={APP_BUILD_ID} />} />
        <Route path="/about" element={<About />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/auth" element={<Auth />} />
        <Route path="/auth/verify-magic-link" element={<VerifyMagicLink />} />
        <Route path="/accept-invitation" element={<AcceptInvitation />} />
        <Route path="/unauthorized" element={<Unauthorized />} />
        <Route path="/designers" element={<Designers />} />
        <Route path="/discover" element={<Index key={APP_BUILD_ID} />} />
        <Route path="/get-verified" element={<Home />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/learn-more" element={<LearnMoreVetting />} />

        {/* SEO Routes for countries and professions */}
        <Route
          path="/country/:countryId"
          element={<Index key={`country-${APP_BUILD_ID}`} />}
        />
        <Route
          path="/profession/:professionId"
          element={<Index key={`profession-${APP_BUILD_ID}`} />}
        />
        <Route
          path="/country/:countryId/profession/:professionId"
          element={<Index key={`country-profession-${APP_BUILD_ID}`} />}
        />

        {/* Support both UUID and username profile routes */}
        <Route path="/profile/:id" element={<ProfileDetail />} />
        <Route path="/u/:username" element={<ProfileDetail key={location.pathname} />} />
        {/* Remove PortfolioItemPage route, modal will handle it */}
        {/* <Route path="/u/:username/portfolio/:itemId" element={<PortfolioItemPage />} /> */}
        <Route path="/claim-profile" element={<ClaimProfile />} />
        {/* Shareable modal route */}
        <Route path="/u/:username/portfolio/:portfolio_slug" element={<ProfileWithPortfolioModal />} />
        <Route path="/services/:slug" element={<ServiceResultsPage />} />
        
        {/* Case Study Preview - Standalone page outside dashboard layout */}
        <Route path="/case-study/preview/:id" element={<CaseStudyPreviewPage />} />
        
        {/* Case Study Editor - Standalone page outside dashboard layout */}
        <Route path="/dashboard/portfolio/case-study/new" element={<CaseStudyEditorPageV2 />} />
        <Route path="/dashboard/portfolio/case-study/:id" element={<CaseStudyEditorPageV2 />} />

        {/* Shot Preview - Standalone page outside dashboard layout */}
        
        {/* Shot Editor - Standalone page outside dashboard layout */}
        <Route path="/dashboard/portfolio/shot/new" element={<ShotEditorPage />} />
        <Route path="/dashboard/portfolio/shot/:id" element={<ShotEditorPage />} />

        {/* Onboarding Route */}
        <Route
          path="/onboarding"
          element={
            <ProtectedRoute>
              <OnboardingRouteGuard />
            </ProtectedRoute>
          }
        />

        {/* Unified App Layout Routes */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <OnboardingWrapper>
                <AppLayout />
              </OnboardingWrapper>
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/dashboard/overview" replace />} />
          <Route path="overview" element={<OverviewPage />} />
          <Route path="profile" element={<ProfilePage />} />
          <Route path="portfolio" element={<PortfolioPage />} />
          <Route path="experience" element={<ExperiencePage />} />
          <Route path="notifications" element={<NotificationsPage />} />
          <Route path="settings" element={<SettingsPage />} />
          {/* Legacy support */}
          <Route path="settings-new" element={<SettingsPage />} />
        </Route>

        {/* Legacy routes - redirect to new dashboard structure */}
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <Navigate to="/dashboard/settings" replace />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings-new"
          element={
            <ProtectedRoute>
              <Navigate to="/dashboard/settings" replace />
            </ProtectedRoute>
          }
        />
        <Route
          path="/notifications"
          element={
            <ProtectedRoute>
              <Navigate to="/dashboard/notifications" replace />
            </ProtectedRoute>
          }
        />

        {/* Admin routes with requireAdmin flag */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute requireAdmin={true}>
              <AdminDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/edit/:id"
          element={
            <ProtectedRoute requireAdmin={true}>
              <AdminEditProfile />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/csv-upload"
          element={
            <ProtectedRoute requireAdmin={true}>
              <CSVUploaderPage />
            </ProtectedRoute>
          }
        />

        {/* Superadmin routes using the same AppLayout */}
        <Route
          path="/superadmin"
          element={
            <ProtectedRoute requireSuperadmin={true}>
              <AppLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<SuperadminDashboard />} />
          <Route path="users" element={<SuperadminUsers />} />
          <Route path="consent-logs" element={<SuperadminConsentLogs />} />
          <Route path="reports" element={<SuperadminReports />} />
          <Route path="report-downloads" element={<Navigate to="/superadmin/reports?tab=downloads" replace />} />
          <Route path="report-versions" element={<Navigate to="/superadmin/reports?tab=versions" replace />} />
        </Route>

        {/* Add redirect for the legacy Supabase password reset path */}
        <Route
          path="/update-password"
          element={
            <Navigate to="/auth?view=update-password&recovery=true" replace />
          }
        />

        <Route path="/learnmore" element={<LearnMore />} />

        <Route path="*" element={<NotFound />} />
      </Routes>
      {/* Modal route: only show if background is set */}
      {state?.background && (
        <Routes>
          <Route path="/u/:username/portfolio/:portfolio_slug" element={<PortfolioItemModalWrapper />} />
        </Routes>
      )}
    </>
  );
};

// Wrapper to extract params and profile context for the modal
const PortfolioItemModalWrapper = () => {
  const { username, portfolio_slug } = useParams<{ username: string; portfolio_slug: string }>();
  const { data: profile } = useProfile(username);
  // Slugify utility (copied from AppRoutes)
  function slugify(text: string) {
    return text
      .toString()
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^\w\-]+/g, '')
      .replace(/--+/g, '-')
      .replace(/^-+/, '')
      .replace(/-+$/, '');
  }
  function getPortfolioSlug(item: any) {
    return item.slug || slugify(item.title || item.id);
  }
  if (!profile) return null;
  const item = profile.portfolio_items?.find((i) => getPortfolioSlug(i) === portfolio_slug);
  if (!item) return null;
  return (
    <PortfolioItemModal
      item={item}
      isOpen={true}
      onClose={() => window.history.back()}
      username={profile.username || ""}
      username_slug={profile.username_slug || ""}
    />
  );
};

export default AppRoutes;
